import { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  useAdminLoanSettings,
  useUpdateAdminLoanSettings,
} from "../../../api/admin";

export default function AdminLoanSettings() {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    min_loan_amount: "",
    max_loan_percentage: "",
    interest_rate: "",
    repayment_period_days: "",
    is_enabled: true,
  });

  const { data: settings, isLoading, refetch } = useAdminLoanSettings();
  const updateSettingsMutation = useUpdateAdminLoanSettings();

  useEffect(() => {
    if (settings) {
      setFormData({
        min_loan_amount: settings.min_loan_amount || "",
        max_loan_percentage: settings.max_loan_percentage || "",
        interest_rate: settings.interest_rate || "",
        repayment_period_days: settings.repayment_period_days || "",
        is_enabled: settings.is_enabled ?? true,
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateSettingsMutation.mutateAsync(formData);
      toast.success("Loan settings updated successfully");
      setIsEditing(false);
      refetch();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to update settings",
      );
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <i className="ri-loader-4-line animate-spin text-4xl text-purple-600"></i>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Loan Settings</h1>
          <p className="text-gray-600 mt-1">
            Configure loan parameters and eligibility criteria
          </p>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
          >
            <i className="ri-edit-line"></i>
            Edit Settings
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Loan Status */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-900">
                Loan System Status
              </label>
              <p className="text-sm text-gray-600">
                Enable or disable the loan feature
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
                disabled={!isEditing}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Minimum Loan Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Minimum Loan Amount (₦)
              </label>
              <input
                type="number"
                name="min_loan_amount"
                value={formData.min_loan_amount}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="e.g., 1000"
              />
              <p className="text-xs text-gray-500 mt-1">
                Minimum amount users can borrow
              </p>
            </div>

            {/* Maximum Loan Percentage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Maximum Loan Percentage (%)
              </label>
              <input
                type="number"
                name="max_loan_percentage"
                value={formData.max_loan_percentage}
                onChange={handleChange}
                disabled={!isEditing}
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="e.g., 50"
              />
              <p className="text-xs text-gray-500 mt-1">
                Percentage of wallet balance eligible for loan
              </p>
            </div>

            {/* Interest Rate */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Interest Rate (%)
              </label>
              <input
                type="number"
                name="interest_rate"
                value={formData.interest_rate}
                onChange={handleChange}
                disabled={!isEditing}
                step="0.01"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="e.g., 5"
              />
              <p className="text-xs text-gray-500 mt-1">
                Interest rate applied to loans
              </p>
            </div>

            {/* Repayment Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Repayment Period (Days)
              </label>
              <input
                type="number"
                name="repayment_period_days"
                value={formData.repayment_period_days}
                onChange={handleChange}
                disabled={!isEditing}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500"
                placeholder="e.g., 30"
              />
              <p className="text-xs text-gray-500 mt-1">
                Number of days to repay the loan
              </p>
            </div>
          </div>

          {isEditing && (
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  if (settings) {
                    setFormData({
                      min_loan_amount: settings.min_loan_amount || "",
                      max_loan_percentage: settings.max_loan_percentage || "",
                      interest_rate: settings.interest_rate || "",
                      repayment_period_days:
                        settings.repayment_period_days || "",
                      is_enabled: settings.is_enabled ?? true,
                    });
                  }
                }}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateSettingsMutation.isPending}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updateSettingsMutation.isPending ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-save-line"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          )}
        </form>
      </div>

      {/* Info Section */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
          <div>
            <h3 className="text-sm font-medium text-blue-900 mb-1">
              How Loan Eligibility Works
            </h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>
                • Users must have completed transactions worth at least the
                minimum loan amount
              </li>
              <li>
                • Maximum eligible loan is calculated as a percentage of their
                total transaction volume
              </li>
              <li>
                • Interest is applied to the loan amount to calculate the
                repayment amount
              </li>
              <li>• Users must repay within the specified repayment period</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
